import { memo } from 'react';
import { useRouter } from 'next/router';

import { Box, Toast, useToast } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyEditV2WebApiHandler_petPolicyDescription } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';

import { useAuth } from '../../../AuthProvider';
import RMWInput from '../../../RMWInput';

const PetPolicyDescriptionField = () => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const initialValue = listingDetails?.buildingAttributes?.petPolicyDescription ?? '';

  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updatePetPolicyDescription, error: updatePetPolicyDescriptionError } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_petPolicyDescription,
    successToastMessage: 'Pet policy description updated',
    meta: { context: { api: 'petPolicyDescription', details: { listingAlias } } },
  });

  const handleBlur = async (event: string | { target: { name: string; value: string } }) => {
    const inputValue = typeof event === 'string' ? event : event.target.value;

    // Don't save if value hasn't changed
    if (inputValue === initialValue) {
      return;
    }

    try {
      await updatePetPolicyDescription({
        postData: { petPolicyDescription: inputValue, listingAlias },
      });
    } catch (err) {
      const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: { lg: '1 1 50%', base: 'auto' } }}>
      <RMWInput
        disabled={isInputFormDisabled}
        errorMessage={(updatePetPolicyDescriptionError as DatarouterError)?.message ?? ''}
        fieldName="petPolicyDescription"
        initialValue={initialValue}
        inputType="textarea"
        label="Pet policy description"
        maxLength="150"
        onBlur={handleBlur}
        showCharacterCount
      />
    </Box>
  );
};

export default memo(PetPolicyDescriptionField);
